import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import {
  FrontendUserPreferences,
  FrontendUserPreferencesApi,
} from '@tourfold/libraries/tf-api-client-frontend-v2';

const AI_ANALYSIS_EXPANDED_STORAGE_KEY = 'tf.document_management.ai_analysis.expanded';
const DEFAULT_AI_ANALYSIS_EXPANDED = true;

const FOLDER_SORT_STORAGE_KEY = 'tf.document_management.folder_sort';
export const DEFAULT_FOLDER_SORT: FolderSortKey = 'name-asc';

const SHOW_TAGS_STORAGE_KEY = 'tf.document_management.show_tags';
const DEFAULT_SHOW_TAGS = false;

export type FolderSortKey =
  | 'name-asc'
  | 'name-desc'
  | 'uploaded-desc'
  | 'uploaded-asc'
  | 'size-desc'
  | 'size-asc';

const FOLDER_SORT_VALUES: readonly FolderSortKey[] = [
  'name-asc',
  'name-desc',
  'uploaded-desc',
  'uploaded-asc',
  'size-desc',
  'size-asc',
];

function isFolderSortKey(value: unknown): value is FolderSortKey {
  return typeof value === 'string' && (FOLDER_SORT_VALUES as readonly string[]).includes(value);
}

@Injectable({
  providedIn: 'root',
})
export class DocumentManagementPreferencesService {
  private readonly preferencesApi = inject(FrontendUserPreferencesApi);
  private readonly _aiAnalysisExpanded = signal(readPersistedExpanded() ?? DEFAULT_AI_ANALYSIS_EXPANDED);
  private readonly _folderSort = signal<FolderSortKey>(readPersistedFolderSort() ?? DEFAULT_FOLDER_SORT);
  private readonly _showTags = signal(readPersistedShowTags() ?? DEFAULT_SHOW_TAGS);

  readonly aiAnalysisExpanded: Signal<boolean> = computed(() => this._aiAnalysisExpanded());
  readonly folderSort: Signal<FolderSortKey> = computed(() => this._folderSort());
  readonly showTags: Signal<boolean> = computed(() => this._showTags());

  constructor() {
    void this.load();
  }

  setAiAnalysisExpanded(expanded: boolean): void {
    this.applyAiAnalysisExpanded(expanded);
    void this.preferencesApi
      .patchCurrentUserPreferences({
        patchFrontendUserPreferences: {
          documentManagement: {
            aiAnalysis: { expanded },
          },
        },
      })
      .then((preferences) => this.applyServerPreferences(preferences))
      .catch(() => {
        // Keep the optimistic local state. The next app/config refresh will reconcile it.
      });
  }

  setFolderSort(sort: FolderSortKey): void {
    this.applyFolderSort(sort);
    void this.preferencesApi
      .patchCurrentUserPreferences({
        patchFrontendUserPreferences: {
          documentManagement: {
            folderSort: sort,
          },
        },
      })
      .then((preferences) => this.applyServerPreferences(preferences))
      .catch(() => {
        // Keep the optimistic local state. The next app/config refresh will reconcile it.
      });
  }

  setShowTags(showTags: boolean): void {
    this.applyShowTags(showTags);
    void this.preferencesApi
      .patchCurrentUserPreferences({
        patchFrontendUserPreferences: {
          documentManagement: {
            showTags,
          },
        },
      })
      .then((preferences) => this.applyServerPreferences(preferences))
      .catch(() => {
        // Keep the optimistic local state. The next app/config refresh will reconcile it.
      });
  }

  private async load(): Promise<void> {
    try {
      const preferences = await this.preferencesApi.getCurrentUserPreferences();
      this.applyServerPreferences(preferences);
    } catch {
      // Keep local fallback/default.
    }
  }

  private applyServerPreferences(preferences: FrontendUserPreferences): void {
    const expanded = preferences.documentManagement?.aiAnalysis?.expanded;
    if (typeof expanded === 'boolean') {
      this.applyAiAnalysisExpanded(expanded);
    }
    const sort = preferences.documentManagement?.folderSort;
    if (isFolderSortKey(sort)) {
      this.applyFolderSort(sort);
    }
    const showTags = preferences.documentManagement?.showTags;
    if (typeof showTags === 'boolean') {
      this.applyShowTags(showTags);
    }
  }

  private applyAiAnalysisExpanded(expanded: boolean): void {
    this._aiAnalysisExpanded.set(expanded);
    persistExpanded(expanded);
  }

  private applyFolderSort(sort: FolderSortKey): void {
    this._folderSort.set(sort);
    persistFolderSort(sort);
  }

  private applyShowTags(showTags: boolean): void {
    this._showTags.set(showTags);
    persistShowTags(showTags);
  }
}

function readPersistedExpanded(): boolean | null {
  try {
    const stored = localStorage.getItem(AI_ANALYSIS_EXPANDED_STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    // ignore
  }
  return null;
}

function persistExpanded(expanded: boolean): void {
  try {
    localStorage.setItem(AI_ANALYSIS_EXPANDED_STORAGE_KEY, String(expanded));
  } catch {
    // ignore
  }
}

function readPersistedFolderSort(): FolderSortKey | null {
  try {
    const stored = localStorage.getItem(FOLDER_SORT_STORAGE_KEY);
    if (isFolderSortKey(stored)) return stored;
  } catch {
    // ignore
  }
  return null;
}

function persistFolderSort(sort: FolderSortKey): void {
  try {
    localStorage.setItem(FOLDER_SORT_STORAGE_KEY, sort);
  } catch {
    // ignore
  }
}

function readPersistedShowTags(): boolean | null {
  try {
    const stored = localStorage.getItem(SHOW_TAGS_STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
  } catch {
    // ignore
  }
  return null;
}

function persistShowTags(showTags: boolean): void {
  try {
    localStorage.setItem(SHOW_TAGS_STORAGE_KEY, String(showTags));
  } catch {
    // ignore
  }
}
